import { useState, useEffect, useRef, useCallback } from 'react';
import SignaturePad from 'signature_pad';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faUser,
  faSignature,
  faKey,
  faSave,
  faEdit,
  faEraser,
  faPalette,
  faEnvelope,
  faLink,
} from '@fortawesome/free-solid-svg-icons';
import { useAuth } from '../../hooks/useAuth';
import { useToast } from '../../hooks/useToast';
import { useTheme, THEMES } from '../../hooks/useTheme';
import type { ThemeId } from '../../hooks/useTheme';
import { useFirmBranding } from '../../hooks/useAdmin';
import api from '../../services/api';
import { Card, CardHeader, CardBody } from '../../components/ui/Card/Card';
import Button from '../../components/ui/Button/Button';
import FormField from '../../components/ui/FormField/FormField';
import PageHeader from '../../components/layout/PageHeader';
import RichTextEditor from '../../components/ui/RichTextEditor/RichTextEditor';
import styles from './PreferencesPage.module.css';

/* ────────────────────────────────────────────────────────── */
/*  Types                                                     */
/* ────────────────────────────────────────────────────────── */
interface UserDetails {
  username: string;
  email: string;
  full_name: string;
  position_in_company: string;
}

interface SignatureData {
  signatureUrl?: string;
  metadata?: { date?: string; time?: string };
}

/* ────────────────────────────────────────────────────────── */
/*  Component                                                 */
/* ────────────────────────────────────────────────────────── */
export default function PreferencesPage() {
  const { user } = useAuth();
  const { showToast } = useToast();
  const { theme, setTheme, isDark } = useTheme();
  const { data: branding } = useFirmBranding();
  const enforced = branding?.brand_enforce_theme === 'true';

  const lightThemes = THEMES.filter((t) => t.mode === 'light');
  const darkThemes = THEMES.filter((t) => t.mode === 'dark');

  // ── User details state ──
  const [username, setUsername] = useState('');
  const [email, setEmail] = useState('');
  const [fullName, setFullName] = useState('');
  const [position, setPosition] = useState('');

  // ── Integration state ──
  const [isOutlookConnected, setIsOutlookConnected] = useState(false);

  // ── Email signature state ──
  const [emailSignature, setEmailSignature] = useState('');
  const [isSavingSignature, setIsSavingSignature] = useState(false);

  // ── Load integration status ──
  useEffect(() => {
    async function check() {
      try {
        const { data } = await api.get<{ connected: boolean }>('/api/emails/check-connection');
        setIsOutlookConnected(data.connected);
      } catch {
        setIsOutlookConnected(false);
      }
    }
    check();
  }, []);

  // ── Load email signature ──
  useEffect(() => {
    async function loadSignature() {
      try {
        const { data } = await api.get<{ email_signature: string | null }>('/api/users/details');
        setEmailSignature(data.email_signature ?? '');
      } catch {
        // no-op — user may not have one yet
      }
    }
    loadSignature();
  }, []);

  const saveEmailSignature = useCallback(async () => {
    setIsSavingSignature(true);
    try {
      await api.put('/api/users/email-signature', { email_signature: emailSignature });
      showToast('Email signature saved', 'success');
    } catch {
      showToast('Failed to save email signature', 'error');
    } finally {
      setIsSavingSignature(false);
    }
  }, [emailSignature, showToast]);

  const handleConnectOutlook = async () => {
    try {
      const { data } = await api.get<{ url: string }>('/api/emails/connect-outlook');
      if (data.url) {
        // Add listener for the popup message
        const handleMessage = (event: MessageEvent) => {
          if (event.data === 'outlook-connected') {
            showToast('Outlook connected successfully!', 'success');
            setIsOutlookConnected(true);
            window.removeEventListener('message', handleMessage);
          }
        };
        window.addEventListener('message', handleMessage);

        window.open(data.url, 'outlook-connect', 'width=600,height=600');
        // Refresh status after a delay or on focus
        setTimeout(async () => {
          const res = await api.get<{ connected: boolean }>('/api/emails/check-connection');
          setIsOutlookConnected(res.data.connected);
        }, 5000);
      }
    } catch {
      showToast('Failed to start Outlook connection', 'error');
    }
  };

  const handleDisconnectOutlook = async () => {
    try {
      await api.post('/api/emails/disconnect-outlook');
      setIsOutlookConnected(false);
      showToast('Outlook disconnected', 'success');
    } catch {
      showToast('Failed to disconnect Outlook', 'error');
    }
  };

  // ── Signature state ──
  const [signatureUrl, setSignatureUrl] = useState<string | null>(null);
  const [signatureMeta, setSignatureMeta] = useState<string>('');
  const [isBlurred, setIsBlurred] = useState(true);
  const [showPad, setShowPad] = useState(false);
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const sigPadRef = useRef<SignaturePad | null>(null);

  // ── Load user details ──
  useEffect(() => {
    const controller = new AbortController();
    async function load() {
      try {
        const { data } = await api.get<UserDetails>('/api/users/details', {
          signal: controller.signal,
        });
        setUsername(data.username ?? '');
        setEmail(data.email ?? user?.email ?? '');
        setFullName(data.full_name ?? '');
        setPosition(data.position_in_company ?? '');
      } catch (err) {
        if (controller.signal.aborted) return;
        // fallback to JWT info
        setUsername(user?.username ?? '');
        setEmail(user?.email ?? '');
      }
    }
    load();
    return () => controller.abort();
  }, [user]);

  // ── Load existing signature ──
  useEffect(() => {
    const controller = new AbortController();
    async function load() {
      try {
        const { data } = await api.get<SignatureData>('/api/users/signature', {
          signal: controller.signal,
        });
        if (data.signatureUrl) {
          setSignatureUrl(data.signatureUrl);
          if (data.metadata?.date && data.metadata?.time) {
            setSignatureMeta(`Last updated: ${data.metadata.date} at ${data.metadata.time}`);
          }
        }
      } catch {
        // no sig yet or aborted — that's fine
      }
    }
    load();
    return () => controller.abort();
  }, []);

  // ── Initialise SignaturePad when pad becomes visible ──
  useEffect(() => {
    if (showPad && canvasRef.current && !sigPadRef.current) {
      sigPadRef.current = new SignaturePad(canvasRef.current, {
        backgroundColor: 'rgb(255, 255, 255)',
        penColor: 'rgb(0, 0, 0)',
        minWidth: 1,
        maxWidth: 2.5,
      });
    }
  }, [showPad]);

  // ── Save signature details ──
  const saveDetails = useCallback(async () => {
    try {
      await api.put('/api/users/details', {
        full_name: fullName,
        position_in_company: position,
      });
      showToast('Details saved successfully', 'success');
    } catch (err: unknown) {
      const msg = err instanceof Error ? err.message : 'Failed to save details';
      showToast(msg, 'error');
    }
  }, [fullName, position, showToast]);

  // ── Save signature canvas ──
  const saveSignature = useCallback(async () => {
    const pad = sigPadRef.current;
    if (!pad || pad.isEmpty()) {
      showToast('Please draw a signature first', 'error');
      return;
    }
    try {
      const dataUrl = pad.toDataURL('image/png');
      const blob = await (await fetch(dataUrl)).blob();
      const form = new FormData();
      form.append('signature', blob, `${user?.username ?? 'sig'}.png`);

      const { data } = await api.post<{ signature_url?: string; signatureUrl?: string }>(
        '/api/users/signature',
        form,
        { headers: { 'Content-Type': 'multipart/form-data' } },
      );

      const url = data.signature_url ?? data.signatureUrl ?? null;
      if (url) setSignatureUrl(url);
      setShowPad(false);
      showToast('Signature saved successfully', 'success');
    } catch (err: unknown) {
      const msg = err instanceof Error ? err.message : 'Failed to save signature';
      showToast(msg, 'error');
    }
  }, [user, showToast]);

  // ── Reset password ──
  const resetPassword = useCallback(async () => {
    try {
      await api.post('/api/auth/forgot-password', { email });
      showToast('Password reset email sent! Check your inbox.', 'success');
    } catch {
      showToast('Failed to send reset email', 'error');
    }
  }, [email, showToast]);

  // ── Clear signature pad ──
  const clearSignature = () => sigPadRef.current?.clear();

  return (
    <>
      <PageHeader title="User Preferences" subtitle="Manage your account settings and signature" />

      <div className={styles.container}>
        {/* ── User Details ── */}
        <Card>
          <CardHeader>
            <h2>
              <FontAwesomeIcon icon={faUser} style={{ color: 'var(--primary-600)' }} /> User Details
            </h2>
          </CardHeader>
          <CardBody>
            <FormField label="Username" value={username} readOnly />
            <FormField label="Email" type="email" value={email} readOnly />

            <div style={{ marginBottom: '0.25rem' }}>
              <label
                style={{
                  display: 'block',
                  marginBottom: '0.5rem',
                  fontWeight: 500,
                  color: 'var(--gray-700)',
                  fontSize: '0.875rem',
                }}
              >
                Password
              </label>
              <div className={styles.passwordRow}>
                <input
                  type="password"
                  value="••••••••••"
                  readOnly
                  style={{
                    flex: 1,
                    padding: '0.75rem',
                    border: '1px solid var(--gray-300)',
                    borderRadius: '8px',
                    fontSize: '0.9375rem',
                    background: 'var(--gray-50)',
                    cursor: 'not-allowed',
                  }}
                />
                <Button onClick={resetPassword}>
                  <FontAwesomeIcon icon={faKey} /> Reset Password
                </Button>
              </div>
            </div>
          </CardBody>
        </Card>

        {/* ── Appearance ── */}
        <Card>
          <CardHeader>
            <h2>
              <FontAwesomeIcon icon={faPalette} style={{ color: 'var(--primary-600)' }} />{' '}
              Appearance
            </h2>
          </CardHeader>
          <CardBody>
            {/* Enforcement notice */}
            {enforced && (
              <div
                style={{
                  background: 'var(--primary-50, #eff6ff)',
                  border: '1px solid var(--primary-200, #bfdbfe)',
                  borderRadius: 'var(--radius-lg)',
                  padding: '12px 16px',
                  marginBottom: 16,
                  fontSize: 'var(--font-size-sm)',
                  color: 'var(--primary-700, #1d4ed8)',
                  display: 'flex',
                  alignItems: 'center',
                  gap: 8,
                }}
              >
                <span>🎨</span>
                <span>
                  <strong>Theme is set by your organisation.</strong> Personal theme selection is
                  currently disabled by your administrator.
                </span>
              </div>
            )}
            {/* Light themes */}
            <div
              style={{
                background: 'var(--gray-50)',
                border: '1px solid var(--gray-200)',
                borderRadius: 'var(--radius-lg)',
                padding: '16px 20px',
                marginBottom: 16,
              }}
            >
              <h3
                style={{
                  margin: '0 0 4px',
                  fontSize: 'var(--font-size-sm)',
                  fontWeight: 700,
                  color: 'var(--gray-800)',
                }}
              >
                ☀️ Light Mode
              </h3>
              <p
                style={{
                  margin: '0 0 12px',
                  fontSize: 'var(--font-size-xs)',
                  color: 'var(--gray-500)',
                }}
              >
                Bright backgrounds with coloured accents
              </p>
              <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap' }}>
                {lightThemes.map((t) => {
                  const isActive = theme === t.id;
                  return (
                    <button
                      key={t.id}
                      onClick={() => !enforced && setTheme(t.id as ThemeId)}
                      disabled={enforced}
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: 12,
                        padding: '12px 16px',
                        borderRadius: 'var(--radius-lg)',
                        border: isActive ? `2.5px solid ${t.swatch}` : '2px solid var(--gray-200)',
                        background: isActive ? `${t.swatch}0d` : 'transparent',
                        cursor: enforced ? 'not-allowed' : 'pointer',
                        opacity: enforced ? 0.6 : 1,
                        transition: 'all 0.2s',
                        outline: isActive ? `2px solid ${t.swatch}40` : 'none',
                        outlineOffset: 2,
                      }}
                    >
                      <span
                        style={{
                          width: 28,
                          height: 28,
                          borderRadius: '50%',
                          flexShrink: 0,
                          background: `linear-gradient(135deg, ${t.swatch} 50%, ${t.swatchAlt ?? t.swatch} 50%)`,
                          border: '2px solid var(--gray-200)',
                          display: 'block',
                          boxShadow: isActive ? `0 0 0 3px ${t.swatch}30` : 'none',
                        }}
                      />
                      <div style={{ textAlign: 'left' }}>
                        <div
                          style={{
                            fontWeight: 600,
                            fontSize: 'var(--font-size-sm)',
                            color: 'var(--gray-800)',
                          }}
                        >
                          {t.label}
                        </div>
                        <div
                          style={{
                            fontSize: 'var(--font-size-xs)',
                            color: 'var(--gray-500)',
                            textTransform: 'capitalize',
                          }}
                        >
                          {t.mode} mode
                        </div>
                      </div>
                      {isActive && (
                        <span
                          style={{
                            marginLeft: 'auto',
                            width: 18,
                            height: 18,
                            borderRadius: '50%',
                            background: t.swatch,
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            color: 'white',
                            fontSize: 11,
                            fontWeight: 700,
                          }}
                        >
                          ✓
                        </span>
                      )}
                    </button>
                  );
                })}
              </div>
            </div>
            {/* Dark themes */}
            <div
              style={{
                background: isDark ? 'var(--gray-100)' : '#1a1a2e',
                border: '1px solid var(--gray-200)',
                borderRadius: 'var(--radius-lg)',
                padding: '16px 20px',
              }}
            >
              <h3
                style={{
                  margin: '0 0 4px',
                  fontSize: 'var(--font-size-sm)',
                  fontWeight: 700,
                  color: isDark ? 'var(--gray-800)' : '#e2e8f0',
                }}
              >
                🌙 Dark Mode
              </h3>
              <p
                style={{
                  margin: '0 0 12px',
                  fontSize: 'var(--font-size-xs)',
                  color: isDark ? 'var(--gray-500)' : '#94a3b8',
                }}
              >
                Dark backgrounds for reduced eye strain
              </p>
              <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap' }}>
                {darkThemes.map((t) => {
                  const isActive = theme === t.id;
                  return (
                    <button
                      key={t.id}
                      onClick={() => !enforced && setTheme(t.id as ThemeId)}
                      disabled={enforced}
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: 12,
                        padding: '12px 16px',
                        borderRadius: 'var(--radius-lg)',
                        border: isActive ? `2.5px solid ${t.swatch}` : '2px solid var(--gray-200)',
                        background: isActive ? `${t.swatch}0d` : 'transparent',
                        cursor: enforced ? 'not-allowed' : 'pointer',
                        opacity: enforced ? 0.6 : 1,
                        transition: 'all 0.2s',
                        outline: isActive ? `2px solid ${t.swatch}40` : 'none',
                        outlineOffset: 2,
                      }}
                    >
                      <span
                        style={{
                          width: 28,
                          height: 28,
                          borderRadius: '50%',
                          flexShrink: 0,
                          background: `linear-gradient(135deg, ${t.swatch} 50%, ${t.swatchAlt ?? t.swatch} 50%)`,
                          border: '2px solid var(--gray-200)',
                          display: 'block',
                          boxShadow: isActive ? `0 0 0 3px ${t.swatch}30` : 'none',
                        }}
                      />
                      <div style={{ textAlign: 'left' }}>
                        <div
                          style={{
                            fontWeight: 600,
                            fontSize: 'var(--font-size-sm)',
                            color: isDark ? 'var(--gray-800)' : '#e2e8f0',
                          }}
                        >
                          {t.label}
                        </div>
                        <div
                          style={{
                            fontSize: 'var(--font-size-xs)',
                            color: isDark ? 'var(--gray-500)' : '#94a3b8',
                            textTransform: 'capitalize',
                          }}
                        >
                          {t.mode} mode
                        </div>
                      </div>
                      {isActive && (
                        <span
                          style={{
                            marginLeft: 'auto',
                            width: 18,
                            height: 18,
                            borderRadius: '50%',
                            background: t.swatch,
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            color: 'white',
                            fontSize: 11,
                            fontWeight: 700,
                          }}
                        >
                          ✓
                        </span>
                      )}
                    </button>
                  );
                })}
              </div>
            </div>
          </CardBody>
        </Card>

        {/* ── Connect to Outlook ── */}
        <Card>
          <CardHeader>
            <h2>
              <FontAwesomeIcon icon={faEnvelope} style={{ color: 'var(--primary-600)' }} /> Connect
              to Outlook
            </h2>
          </CardHeader>
          <CardBody>
            <div
              style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                padding: '16px',
                background: 'var(--gray-50)',
                borderRadius: '12px',
                border: '1px solid var(--gray-200)',
              }}
            >
              <div style={{ display: 'flex', alignItems: 'center', gap: '16px' }}>
                <div
                  style={{
                    width: '40px',
                    height: '40px',
                    borderRadius: '8px',
                    background: '#0078d4',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    color: 'white',
                    fontSize: '20px',
                  }}
                >
                  <FontAwesomeIcon icon={faEnvelope} />
                </div>
                <div>
                  <div style={{ fontWeight: 600, color: 'var(--gray-800)' }}>Microsoft Outlook</div>
                  <div style={{ fontSize: '0.8rem', color: 'var(--gray-500)' }}>
                    {isOutlookConnected
                      ? 'Connected - Professional email enabled'
                      : 'Not connected - Connect to send professional emails'}
                  </div>
                </div>
              </div>

              {isOutlookConnected ? (
                <Button variant="secondary" onClick={handleDisconnectOutlook}>
                  Disconnect
                </Button>
              ) : (
                <Button onClick={handleConnectOutlook}>
                  <FontAwesomeIcon icon={faLink} /> Connect
                </Button>
              )}
            </div>

            {/* ── Email Signature Editor ── */}
            {isOutlookConnected && (
              <div style={{ marginTop: '24px' }}>
                <div style={{ marginBottom: '8px' }}>
                  <div
                    style={{
                      fontWeight: 600,
                      color: 'var(--text-main, var(--gray-800))',
                      marginBottom: '4px',
                    }}
                  >
                    Personal Email Signature
                  </div>
                  <div
                    style={{
                      fontSize: '0.8rem',
                      color: 'var(--text-muted, var(--gray-500))',
                      marginBottom: '12px',
                    }}
                  >
                    This signature will be automatically added to emails you send via Outlook
                    through the system. Supports rich formatting, images, tables, and links.
                  </div>
                </div>
                <RichTextEditor
                  content={emailSignature}
                  onChange={setEmailSignature}
                  placeholder="Write your email signature here — e.g. Kind regards, John Doe | Solicitor | Justizia Law"
                />
                {emailSignature && (
                  <div
                    className={styles.emailSignaturePreview}
                    data-testid="email-signature-preview"
                  >
                    <div className={styles.emailSignaturePreviewTitle}>
                      Stored Signature Preview
                    </div>
                    <div className={styles.emailSignaturePreviewHint}>
                      This renders the saved HTML without the editor toolbar styling.
                    </div>
                    <div
                      className={styles.emailSignaturePreviewBody}
                      dangerouslySetInnerHTML={{ __html: emailSignature }}
                    />
                  </div>
                )}
                <div style={{ marginTop: '12px' }}>
                  <Button onClick={saveEmailSignature} disabled={isSavingSignature}>
                    <FontAwesomeIcon icon={faSave} />
                    {isSavingSignature ? ' Saving...' : ' Save Signature'}
                  </Button>
                </div>
              </div>
            )}
          </CardBody>
        </Card>

        {/* ── Signature Details ── */}
        <Card>
          <CardHeader>
            <h2>
              <FontAwesomeIcon icon={faSignature} style={{ color: 'var(--primary-600)' }} />{' '}
              Configure Signature Details
            </h2>
          </CardHeader>
          <CardBody>
            <FormField
              label="Full Name"
              value={fullName}
              onChange={(e) => setFullName(e.target.value)}
              placeholder="Enter your full name"
            />
            <FormField
              label="Position in Company"
              value={position}
              onChange={(e) => setPosition(e.target.value)}
              placeholder="Enter your position"
            />
            <Button onClick={saveDetails}>
              <FontAwesomeIcon icon={faSave} /> Save Details
            </Button>

            <hr className={styles.divider} />

            {/* Current signature preview */}
            {signatureUrl && (
              <div className={styles.signaturePreview}>
                <p className={styles.muted}>Current signature:</p>
                <img
                  src={signatureUrl}
                  alt="Current Signature"
                  className={`${styles.signatureImg} ${isBlurred ? styles.blurred : ''}`}
                  onClick={() => setIsBlurred((v) => !v)}
                  title={isBlurred ? 'Click to show' : 'Click to hide'}
                />
                {signatureMeta && <p className={styles.metadata}>{signatureMeta}</p>}
              </div>
            )}

            {/* Toggle button */}
            <Button variant="secondary" onClick={() => setShowPad((v) => !v)}>
              <FontAwesomeIcon icon={faEdit} />{' '}
              {showPad ? 'Hide Signature Pad' : 'Show Signature Pad'}
            </Button>

            {/* Signature pad */}
            {showPad && (
              <>
                <div className={styles.signaturePadWrapper}>
                  <canvas ref={canvasRef} width={600} height={200} />
                </div>
                <div className={styles.signatureActions}>
                  <Button variant="secondary" onClick={clearSignature}>
                    <FontAwesomeIcon icon={faEraser} /> Clear
                  </Button>
                  <Button onClick={saveSignature}>
                    <FontAwesomeIcon icon={faSave} /> Save Signature
                  </Button>
                </div>
              </>
            )}
          </CardBody>
        </Card>
      </div>
    </>
  );
}
